import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { z } from "zod";

import { sanitizeInput } from "@/helpers/helpers";
import { Button } from "@/newComponents/button/Button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/newComponents/form/Form";
import { IconBin, IconChevronLeft, IconChevronRight, IconTick } from "@/newComponents/icon/Icon";
import { FormCheckbox } from "@/newComponents/input/FormCheckbox";
import { FormInput } from "@/newComponents/input/FormInput";
import { FormMultipleInput } from "@/newComponents/input/FormMultipleInput";
import { FormNumberInput } from "@/newComponents/input/FormNumericalInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/newComponents/select/Select";
import Heading from "@/newComponents/typography/Heading";
import Text from "@/newComponents/typography/Text";
import { useUIStateStore } from "@/store/uiStateStore";

import useAlbumStore from "../store/albumStore";
import useSongWriterAssignmentStore from "../store/songWriterAssignmentStore";
import useWriterStore from "../store/writerStore";

// Zod schema for song form validation
const songFormSchema = z.object({
  // Song details
  songTitle: z
    .string()
    .min(1, "Song title is required")
    .max(200, "Song title must be less than 200 characters")
    .refine((value) => {
      // Check if sanitized version is different from original
      const sanitized = sanitizeInput(value);
      const safeSanitized = sanitized || "";
      return safeSanitized === value;
    }, "Song title contains invalid characters"),

  durationMins: z
    .number()
    .min(0, "Duration minutes cannot be negative")
    .max(60, "Duration minutes must be less than 60"),

  durationSecs: z
    .number()
    .min(0, "Duration seconds cannot be negative")
    .max(59, "Duration seconds must be less than 60"),

  alternativeTitles: z
    .array(z.string())
    .refine((values) => {
      // Check if all values are valid - this ensures we can provide a proper error message
      return values.every((value) => {
        if (!value || value.trim() === "")
          return true; // Empty strings are allowed
        const sanitized = sanitizeInput(value);
        const safeSanitized = sanitized || "";
        return safeSanitized === value;
      });
    }, "One or more alternative titles contain invalid characters"),

  performingArtists: z
    .array(z.string().min(1, "Performing artist name is required").max(100, "Artist name must be less than 100 characters"))
    .min(1, "At least one performing artist is required"),

  // Writer validation
  writers: z
    .array(z.object({
      writercode: z.string(),
      writername: z.string(),
      writershare: z.number().min(0).max(100),
      writerdesignationcode: z.number().min(1).max(3),
      righttocollect: z.boolean(),
    }))
    .min(1, "At least one writer is required")
    .refine((writers) => {
      const totalShares = writers.reduce((sum, writer) => sum + writer.writershare, 0);
      return Math.abs(totalShares - 100) < 0.01;
    }, "Total writer shares must equal 100%")
    .refine((writers) => {
      return writers.some(writer => writer.righttocollect === true);
    }, "At least one writer must have 'Right To Collect' enabled"),
});

type SongFormData = z.infer<typeof songFormSchema>;

// Types for writer splits with shares
export type WriterShareData = {
  writercode: string;
  writername: string;
  writershare: number;
  writerdesignationcode: number;
  righttocollect: boolean;
};

export type SongShareData = {
  songId: string;
  songTitle: string;
  durationMins: number;
  durationSecs: number;
  alternativeTitles: string[];
  performingArtists: string[];
  writers: WriterShareData[];
};

export default function DetailsAndSharesStep() {
  const { t } = useTranslation("album");
  const { tracks: selectedTracks } = useAlbumStore();
  const { assignments } = useSongWriterAssignmentStore();
  const { selectedWriters, availableWriters } = useWriterStore();
  const { songShares, setSongShares, currentSongIndex, setCurrentSongIndex } = useAlbumStore();
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Writer designation codes mapping
  const DESIGNATION_OPTIONS = [
    { value: 1, label: t("author") ?? "Author" },
    { value: 2, label: t("composer") ?? "Composer" },
    { value: 3, label: t("composer-author") ?? "Composer/Author" },
  ];

  // Current song being edited
  // Using currentSongIndex from store instead of local state

  // Form for current song
  const form = useForm<SongFormData>({
    resolver: zodResolver(songFormSchema),
    mode: "onChange",
    defaultValues: {
      songTitle: "",
      durationMins: 0,
      durationSecs: 0,
      alternativeTitles: [],
      performingArtists: [],
      writers: [],
    },
  });

  // Get all songs that have writers assigned
  const songsWithWriters = React.useMemo(() => {
    return selectedTracks
      .filter(track => track.isSelected)
      .map((track) => {
        const songAssignments = assignments[track.id];
        const assignedWriters = selectedWriters
          .filter(writer => songAssignments?.[writer.writercode])
          .map((selectedWriter) => {
            const fullWriter = availableWriters.find(w => w.writercode === selectedWriter.writercode);
            return fullWriter || selectedWriter;
          });

        return {
          songId: track.id,
          songTitle: track.name,
          assignedWriters,
        };
      })
      .filter(song => song.assignedWriters.length > 0);
  }, [selectedTracks, selectedWriters, availableWriters, assignments]);

  // Ensure currentSongIndex is within bounds
  React.useEffect(() => {
    if (songsWithWriters.length > 0 && (currentSongIndex >= songsWithWriters.length || currentSongIndex < 0)) {
      setCurrentSongIndex(0);
    }
  }, [songsWithWriters.length, currentSongIndex, setCurrentSongIndex]);

  // Initialize song shares from assignments if not already set
  useEffect(() => {
    // Only initialize if we have songs with writers and they're not already in the store
    if (songsWithWriters.length === 0)
      return;

    // Get current state from the store to avoid dependency issues
    const currentSongShares = useAlbumStore.getState().songShares;
    const currentSongIds = songsWithWriters.map(song => song.songId);
    const existingSongIds = Object.keys(currentSongShares);
    const missingIds = currentSongIds.filter(id => !existingSongIds.includes(id));

    if (missingIds.length === 0)
      return; // Nothing to initialize

    const initialShares: Record<string, SongShareData> = { ...currentSongShares };

    missingIds.forEach((songId) => {
      const song = songsWithWriters.find(s => s.songId === songId);
      if (!song)
        return;

      const track = selectedTracks.find(t => t.id === songId);

      const songWriters: WriterShareData[] = song.assignedWriters.map((writer) => {
        const displayName = "writername" in writer && writer.writername
          ? writer.writername
          : ("firstname" in writer && "lastname" in writer)
              ? `${writer.firstname ?? ""} ${writer.lastname ?? ""}`.trim()
              // : "Unknown Writer";
              : t("unknown-writer") ?? "Unknown Writer";

        return {
          writercode: writer.writercode,
          writername: displayName,
          writershare: 0,
          writerdesignationcode: 3, // Default to "Composer/Author"
          righttocollect: false,
        };
      });

      // Sanitize song title and prepare performing artists
      const performingArtists = track?.artists?.map((a) => {
        const sanitized = sanitizeInput(a.name);
        // Keep the original name if sanitization returns empty/null, otherwise use sanitized
        return sanitized && sanitized.trim() ? sanitized : a.name;
      }).filter(name => name && name.trim()) || [];

      // Initialize song data with track information
      initialShares[songId] = {
        songId,
        songTitle: song.songTitle, // Keep original title, don't sanitize automatically
        durationMins: track ? Math.floor(track.duration_ms / 60000) : 0,
        durationSecs: track ? Math.floor((track.duration_ms % 60000) / 1000) : 0,
        alternativeTitles: [],
        performingArtists,
        writers: songWriters,
      };
    });

    setSongShares(initialShares);
  }, [songsWithWriters, selectedTracks, setSongShares, t]);

  const currentSong = songsWithWriters[currentSongIndex];
  const currentSongData = currentSong != null ? songShares[currentSong.songId] : null;

  // Helper function to check if song title has invalid characters
  const getTitleValidationInfo = (title: string) => {
    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      hasInvalidChars,
      suggestedTitle: safeSanitized || (t("no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Helper function to validate alternative titles
  const validateAlternativeTitle = (title: string) => {
    if (title.length === 0)
      return { isValid: true, suggestedTitle: "" };

    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      isValid: !hasInvalidChars,
      suggestedTitle: safeSanitized.length > 0 ? safeSanitized : (t("no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Update form when current song changes
  useEffect(() => {
    if (currentSongData) {
      form.reset({
        songTitle: currentSongData.songTitle,
        durationMins: currentSongData.durationMins,
        durationSecs: currentSongData.durationSecs,
        alternativeTitles: currentSongData.alternativeTitles,
        performingArtists: currentSongData.performingArtists,
        writers: currentSongData.writers,
      });
    }
  }, [currentSongData, form]);

  // Update a specific song's data
  const updateSongData = (songId: string, updates: Partial<SongShareData>) => {
    const updatedShares = {
      ...songShares,
      [songId]: {
        ...songShares[songId],
        ...updates,
      },
    };
    setSongShares(updatedShares);
  };

  // Update a writer's data for the current song
  const updateWriter = (writerIndex: number, updates: Partial<WriterShareData>) => {
    if (currentSong == null || currentSongData == null)
      return;

    const updatedWriters = [...currentSongData.writers];
    updatedWriters[writerIndex] = { ...updatedWriters[writerIndex], ...updates };

    updateSongData(currentSong.songId, { writers: updatedWriters });
  };

  // Remove a writer from the current song
  const removeWriter = (writerIndex: number) => {
    if (currentSong == null || currentSongData == null)
      return;
    if (currentSongData.writers.length <= 1)
      return; // Don't allow removing the last writer

    const updatedWriters = currentSongData.writers.filter((_, index) => index !== writerIndex);
    updateSongData(currentSong.songId, { writers: updatedWriters });
  };

  // Distribute shares equally among all writers
  const distributeSharesEqually = () => {
    if (currentSong == null || currentSongData == null || currentSongData.writers.length === 0)
      return;

    const numberWriters = currentSongData.writers.length;
    const share = Number((100 / numberWriters).toFixed(2));

    const updatedWriters = currentSongData.writers.map((writer, index) => {
      let writershare: number;
      if (index === numberWriters - 1) {
        // For the last writer, calculate the remaining share to ensure total is exactly 100
        writershare = Number((100 - index * share).toFixed(2));
      }
      else {
        writershare = share;
      }

      return {
        ...writer,
        writershare,
      };
    });

    updateSongData(currentSong.songId, { writers: updatedWriters });
  };

  // Check if current song data is valid
  const isCurrentSongValid = (): boolean => {
    if (!currentSongData || currentSongData.writers.length === 0)
      return false;

    const totalShares = currentSongData.writers.reduce((sum, writer) => sum + writer.writershare, 0);
    const hasRTC = currentSongData.writers.some(writer => writer.righttocollect === true);

    return Math.abs(totalShares - 100) < 0.01 && hasRTC;
  };

  // Count songs with same writers for apply button
  const songsWithSameWriters = React.useMemo(() => {
    if (currentSong == null || currentSongData == null)
      return 0;

    const currentWriterCodes = currentSongData.writers.map(w => w.writercode).sort();

    return songsWithWriters.filter((song) => {
      if (song.songId === currentSong.songId)
        return false;
      const songWriterCodes = song.assignedWriters.map(w => w.writercode).sort();
      return JSON.stringify(currentWriterCodes) === JSON.stringify(songWriterCodes);
    }).length;
  }, [currentSong, currentSongData, songsWithWriters]);

  // Apply current song's writer configuration to songs with same writers
  const applyToSongsWithSameWriters = () => {
    if (currentSong == null || currentSongData == null || !isCurrentSongValid())
      return;

    const currentWriterCodes = currentSongData.writers.map(w => w.writercode).sort();
    let appliedCount = 0;

    // Create a deep copy of the updated song shares to batch update
    const updatedShares = { ...songShares };

    songsWithWriters.forEach((song) => {
      if (song.songId === currentSong.songId)
        return;

      const songWriterCodes = song.assignedWriters.map(w => w.writercode).sort();

      if (JSON.stringify(currentWriterCodes) === JSON.stringify(songWriterCodes)) {
        // Create a deep copy of the current song's writers with all properties
        const writersCopy = currentSongData.writers.map(writer => ({
          writercode: writer.writercode,
          writername: writer.writername,
          writershare: writer.writershare,
          writerdesignationcode: writer.writerdesignationcode,
          righttocollect: writer.righttocollect, // Explicitly copy RTC status
        }));

        // Update the song data in our batch
        updatedShares[song.songId] = {
          ...updatedShares[song.songId],
          writers: writersCopy,
        };
        appliedCount++;
      }
    });

    // Apply all updates at once
    if (appliedCount > 0) {
      setSongShares(updatedShares);
      toast.success(`Applied writer settings to ${appliedCount} song${appliedCount > 1 ? "s" : ""} with matching writers`);
    }
  };

  // Calculate total shares for current song
  const totalShares = currentSongData?.writers
    ? currentSongData.writers.reduce((sum, writer) => sum + writer.writershare, 0)
    : 0;

  // Navigation
  const goToPreviousSong = () => {
    if (currentSongIndex > 0) {
      setCurrentSongIndex(currentSongIndex - 1);
    }
  };

  const goToNextSong = () => {
    if (currentSongIndex < songsWithWriters.length - 1) {
      setCurrentSongIndex(currentSongIndex + 1);
    }
  };

  if (songsWithWriters.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className={`text-sm ${isTunecore ? "text-gray-300" : "text-gray-500"}`}>
          {t("no-songs-with-writers")}
        </Text>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "text-old-sentric-dark-gray"}`}>

      <Heading as="h3" className="inline-flex items-center justify-between w-full mt-2 font-semibold max-md:flex-col">
        <span className="md:w-1/2">
          {currentSong?.songTitle || (t("unknown-song") ?? "Unknown Song")}
        </span>
        <span className="max-sm:text-base">
          {currentSongIndex + 1}
          {" / "}
          {songsWithWriters.length}
        </span>
      </Heading>

      {/* Current Song Editing */}
      {currentSongData && (
        <Form {...form}>
          <div className="space-y-6">
            {/* Song Details Section */}
            <div className="space-y-4">
              <Text as="subtitle">
                {t("song-details") ?? "Song Details"}
              </Text>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="songTitle"
                    render={({ field }) => {
                      const titleValidation = getTitleValidationInfo(field.value);
                      return (
                        <FormItem>
                          {/* <FormLabel>Song Title</FormLabel> */}
                          <FormLabel>
                            {t("song-title") ?? "Song Title"}
                          </FormLabel>
                          <FormControl>
                            <FormInput
                              {...field}
                              // placeholder="Enter song title"
                              placeholder={t("enter-song-title") ?? "Enter song title"}
                              className={titleValidation.hasInvalidChars ? `border-red-500 ${isTunecore ? "bg-red-900/20" : "bg-red-50"}` : ""}
                              onChange={(e) => {
                                field.onChange(e);
                                if (currentSong != null) {
                                  updateSongData(currentSong.songId, { songTitle: e.target.value });
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                          {titleValidation.hasInvalidChars && (
                            <div className={`mt-2 space-y-2 p-3 rounded-md border ${isTunecore ? "bg-[#282828] border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                              <p className={`text-sm mb-2 ${isTunecore ? "text-yellow-400" : "text-yellow-800"}`}>
                                {/* <strong>Warning:</strong> */}
                                <strong>
                                  {t("warning") ?? "Warning"}
                                  :
                                </strong>
                                {" "}
                                {t("song-title-invalid-characters") ?? "This song title contains invalid characters for our system and can't be registered."}
                                {" "}
                                {t("suggested-title") ?? "Suggested title"}
                                : "
                                {titleValidation.suggestedTitle}
                                "
                              </p>
                              {titleValidation.suggestedTitle !== "(no valid characters found)" && (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => {
                                    // Only apply suggested title if it's not our fallback message
                                    if (titleValidation.suggestedTitle !== "(no valid characters found)") {
                                      field.onChange(titleValidation.suggestedTitle);
                                      if (currentSong != null) {
                                        updateSongData(currentSong.songId, { songTitle: titleValidation.suggestedTitle });
                                      }
                                    }
                                  }}
                                  // label="Use Suggested Title"
                                  label={t("use-suggested-title") ?? "Use Suggested Title"}
                                />
                              )}
                            </div>
                          )}
                        </FormItem>
                      );
                    }}
                  />
                </div>

                <div className="flex gap-2">
                  <FormField
                    control={form.control}
                    name="durationMins"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>
                          {t("duration-minutes") ?? "Duration (Minutes)"}
                        </FormLabel>
                        <FormControl>
                          <FormNumberInput
                            label=""
                            min={0}
                            max={60}
                            className="w-full"
                            placeholder="0"
                            value={field.value}
                            onChange={(e) => {
                              const value = Number(e.target.value) || 0;
                              field.onChange(value);
                              if (currentSong != null) {
                                updateSongData(currentSong.songId, { durationMins: value });
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="durationSecs"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>
                          {t("duration-seconds") ?? "Duration (Seconds)"}
                        </FormLabel>
                        <FormControl>
                          <FormNumberInput
                            label=""
                            min={0}
                            max={59}
                            className="w-full"
                            placeholder="0"
                            value={field.value}
                            onChange={(e) => {
                              const value = Number(e.target.value) || 0;
                              field.onChange(value);
                              if (currentSong != null) {
                                updateSongData(currentSong.songId, { durationSecs: value });
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="performingArtists"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Performing Artists</FormLabel>
                    <FormControl>
                      <FormMultipleInput
                        values={field.value}
                        onChange={(values: string[]) => {
                          field.onChange(values);
                          if (currentSong != null) {
                            updateSongData(currentSong.songId, { performingArtists: values });
                          }
                        }}
                        placeholder={t("enter-performing-artists") ?? "Enter performing artist names"}
                        addButtonText={t("add-performing-artist") ?? "Add Performing Artist"}
                        minItems={0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="alternativeTitles"
                render={({ field }) => {
                  // Check for invalid characters in any alternative title
                  const invalidTitles = field.value
                    .map((title, index) => ({ title, index, ...validateAlternativeTitle(title) }))
                    .filter(item => !item.isValid && item.title.length > 0);

                  return (
                    <FormItem>
                      <FormLabel>
                        {t("alternative-titles") ?? "Alternative Titles"}
                      </FormLabel>
                      <FormControl>
                        <FormMultipleInput
                          values={field.value}
                          onChange={(values: string[]) => {
                            field.onChange(values);
                            if (currentSong != null) {
                              updateSongData(currentSong.songId, { alternativeTitles: values });
                            }
                          }}
                          // placeholder="Enter alternative titles"
                          placeholder={t("enter-alternative-titles") ?? "Enter alternative titles"}
                          // addButtonText="Add Alternative Title"
                          addButtonText={t("add-alternative-title") ?? "Add Alternative Title"}
                          minItems={0}
                        />
                      </FormControl>
                      <FormMessage />

                      {/* Show validation warnings for invalid alternative titles */}
                      {invalidTitles.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {invalidTitles.map(item => (
                            <div key={item.index} className={`p-3 rounded-md border ${isTunecore ? "bg-[#282828] border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                              <p className={`text-sm mb-2 ${isTunecore ? "text-yellow-400" : "text-yellow-800"}`}>
                                <strong>
                                  {t("warning") ?? "Warning"}
                                  :
                                </strong>
                                {" "}
                                {t("alternative-title") ?? "Alternative title"}
                                {" "}
                                "
                                {item.title}
                                "
                                {" "}
                                {t("contains-invalid-characters") ?? "contains invalid characters."}
                                {" "}
                                {t("suggested-title") ?? "Suggested title"}
                                : "
                                {item.suggestedTitle}
                                "
                              </p>
                              {item.suggestedTitle !== "(no valid characters found)" && (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => {
                                    // Only apply suggested title if it's not our fallback message
                                    if (item.suggestedTitle !== "(no valid characters found)") {
                                      const newValues = [...field.value];
                                      newValues[item.index] = item.suggestedTitle;
                                      field.onChange(newValues);
                                      if (currentSong != null) {
                                        updateSongData(currentSong.songId, { alternativeTitles: newValues });
                                      }
                                    }
                                  }}
                                  // label="Use Suggested Title"
                                  label={t("use-suggested-title") ?? "Use Suggested Title"}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </FormItem>
                  );
                }}
              />
            </div>
          </div>
        </Form>
      )}

      {/* Writer Shares Section */}
      {currentSongData && (
        <div className="space-y-4">
          <div className="flex flex-col items-center justify-between max-md:gap-4 md:flex-row">
            <div className="flex flex-col items-start text-start">
              <Text as="subtitle">
                {t("writer-shares-roles") ?? "Writer Shares & Roles"}
              </Text>
              <div className="flex flex-col">
                <Text
                  as="p"
                  className={`font-medium ${
                    Math.abs(totalShares - 100) <= 0.01 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {t("total-shares") ?? "Total Shares"}
                  :
                  {" "}
                  {totalShares.toFixed(2)}
                  %
                </Text>
                {!currentSongData.writers.some(w => w.righttocollect) && (
                  <Text as="p" className={`text-sm ${isTunecore ? "text-red-400" : "text-red-600"}`}>

                    {t("at-least-one-writer-rtc") ?? "At least one writer must have 'Right To Collect' enabled."}
                  </Text>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center gap-4 md:flex-row">
              <Button
                type="button"
                className="w-max"
                onClick={distributeSharesEqually}
                variant="secondary"
                disabled={currentSongData.writers.length === 0}
                label={t("distribute-equally") ?? "Distribute Equally"}
              />
              {songsWithSameWriters > 0 && (
                <Button
                  type="button"
                  onClick={applyToSongsWithSameWriters}
                  variant="primary"
                  disabled={!isCurrentSongValid()}
                  label={t("apply-to-other-songs", {
                    count: songsWithSameWriters,
                  }) ?? `Apply to ${songsWithSameWriters} other song${songsWithSameWriters > 1 ? "s" : ""} with same writers`}
                  icon={<IconTick className={isTunecore ? "stroke-white stroke-[3]" : "stroke-black stroke-[3]"} />}
                />
              )}
            </div>
          </div>

          {/* Writers List */}
          <div className="space-y-3">
            {currentSongData.writers.map((writer, index) => (
              <div key={writer.writercode} className={`flex flex-col md:flex-row items-center gap-4 p-4 rounded-lg shadow-sm transition-colors ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border border-sentric-mid-grey"}`}>
                <div className="flex-1">
                  <Text as="p" className="font-semibold">
                    {writer.writername}
                  </Text>
                  <Text as="p">
                    {writer.writercode}
                  </Text>

                </div>

                <div className="flex flex-col items-center gap-5 md:flex-row">
                  <Select
                    value={writer.writerdesignationcode.toString()}
                    onValueChange={value => updateWriter(index, {
                      writerdesignationcode: Number.parseInt(value, 10),
                    })}
                  >
                    <SelectTrigger className="w-full md:w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DESIGNATION_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <FormNumberInput
                    type="number"
                    value={writer.writershare.toString()}
                    onChange={e => updateWriter(index, { writershare: Number.parseFloat(e.target.value) || 0 })}
                    placeholder={t("share-percentage") ?? "Share %"}
                    className="md:w-20"
                    min={0}
                    max={100}
                    showPercentage={true}
                    step={0.01}
                  />

                  <FormCheckbox
                    label={t("right-to-collect") ?? "Right To Collect"}
                    checked={writer.righttocollect}
                    onChange={e => updateWriter(index, { righttocollect: e.target.checked })}
                  />

                  <Button
                    type="button"
                    onClick={() => removeWriter(index)}
                    variant="link"
                    className="text-red-600 hover:text-red-700"
                    disabled={currentSongData.writers.length <= 1}
                    icon={<IconBin className="w-5 h-5" />}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Share Total Display */}
          <div>

            <div className="flex flex-col justify-center gap-6 p-4 rounded-lg md:flex-row md:items-center">

              <Button
                onClick={goToPreviousSong}
                disabled={currentSongIndex === 0}
                variant="secondary"
                className="min-w-32"
                label={(
                  <div className="flex items-center gap-2">
                    <IconChevronLeft className={isTunecore ? "stroke-white" : "stroke-black"} />
                    {t("previous-song") ?? "Previous"}
                  </div>
                )}
              />

              <Button
                onClick={goToNextSong}
                disabled={currentSongIndex === songsWithWriters.length - 1}
                variant="secondary"
                className="min-w-32"
                label={(
                  <div className="flex items-center gap-2">
                    {t("next-song") ?? "Next"}
                    <IconChevronRight className={isTunecore ? "stroke-white" : "stroke-black"} />
                  </div>
                )}
              />

            </div>

          </div>
        </div>
      )}
    </div>
  );
}
